import type { ReactNode } from 'react';

import { assetUrl } from '../assets/asset-manifest.js';
import type { AssetName } from '../assets/asset-manifest.js';

export interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  /**
   * Optional decorative illustration above the title (docs/UI_GUIDELINES.md
   * §8). Decorative only — the title and description carry the meaning.
   */
  illustration?: AssetName;
  /**
   * Heading level for `title`. Defaults to `2`, which is right for an empty
   * state standing in for a section's content on a page that already has its
   * own `<h1>`. A page whose *entire* content is an empty state (the 404) must
   * pass `1`, or that page ends up with no `<h1>` at all.
   */
  headingLevel?: 1 | 2;
}

export function EmptyState({
  title,
  description,
  action,
  illustration,
  headingLevel = 2,
}: EmptyStateProps) {
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  return (
    <div className="rounded-lg border border-dashed border-border bg-surfaceElevated px-5 py-8 text-center">
      {illustration && (
        <img
          src={assetUrl(illustration)}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="mx-auto mb-4 h-24 w-auto opacity-90"
        />
      )}
      <Heading className="section-title">{title}</Heading>
      {description && (
        <div className="mx-auto mt-2 max-w-readable text-sm text-textSecondary">{description}</div>
      )}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}
